import { chart } from "./gate"

export const LIMIT = 5
const DAY = 24 * 60 * 60 * 1000

function parts(date) {
  return date.split("-").map(Number)
}

function utc(date) {
  const [year, month, day] = parts(date)
  return Date.UTC(year, month - 1, day)
}

function format(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

export function addMonths(date, offset) {
  const [year, month, day] = parts(date)
  const target = new Date(Date.UTC(year, month - 1 + offset, 1))
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  return format(target.getUTCFullYear(), target.getUTCMonth() + 1, Math.min(day, last))
}

export function today() {
  const now = new Date()
  return format(now.getFullYear(), now.getMonth() + 1, now.getDate())
}

function daysBetween(from, to) {
  return (utc(to) - utc(from)) / DAY
}

function firstIndexAfter(rows, test) {
  let low = 0
  let high = rows.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (test(rows[middle][0])) high = middle
    else low = middle + 1
  }
  return low
}

function schedule(start, frequency, asOf) {
  if (frequency !== "monthly") return [start]

  const months = []
  for (let offset = 0; addMonths(start, offset) <= asOf; offset++) months.push(addMonths(start, offset))
  return months
}

function rowsInEuros(data, rates, start, asOf) {
  const rows = []
  let rateIndex = -1

  data.dates.forEach((date, index) => {
    if (date < start || date > asOf) return

    if (!rates) {
      rows.push([date, data.closes[index]])
      return
    }

    while (rateIndex + 1 < rates.dates.length && rates.dates[rateIndex + 1] <= date) rateIndex++
    if (rateIndex < 0) return
    rows.push([date, data.closes[index] / rates.closes[rateIndex]])
  })

  return rows
}

function annual(amount, purchases, finish, worth) {
  if (daysBetween(purchases[0][0], finish) < 365) return null

  const flows = purchases.map(([date]) => daysBetween(date, finish) / 365)
  let low = -0.99
  let high = 10.0
  for (let step = 0; step < 60; step++) {
    const rate = (low + high) / 2
    const grown = flows.reduce((sum, years) => sum + amount * (1 + rate) ** years, 0)
    if (grown > worth) high = rate
    else low = rate
  }
  return (low + high) / 2 * 100
}

function worstDrop(rows) {
  let peak = rows[0][1]
  return rows.reduce((worst, [, price]) => {
    peak = Math.max(peak, price)
    return Math.min(worst, (price - peak) / peak * 100)
  }, 0)
}

function bestYear(rows) {
  let best = null
  let index = 0

  while (index < rows.length) {
    const year = rows[index][0].slice(0, 4)
    const opening = index === 0 ? rows[0][1] : rows[index - 1][1]
    while (index + 1 < rows.length && rows[index + 1][0].slice(0, 4) === year) index++
    const change = (rows[index][1] - opening) / opening * 100
    if (!best || change > best[1]) best = [Number(year), change]
    index++
  }

  return best
}

function result(data, rows, amount, dates) {
  if (rows.length === 0 || rows[0][0] >= addMonths(dates[0], 1)) return null

  const purchases = dates.flatMap((date) => {
    const purchase = rows[firstIndexAfter(rows, (day) => day >= date)]
    return purchase && purchase[0] < addMonths(date, 1) ? [purchase] : []
  })

  let units = 0
  let putIn = 0
  let next = 0
  const series = rows.map(([date, price]) => {
    while (next < purchases.length && purchases[next][0] <= date) {
      units += amount / purchases[next][1]
      putIn += amount
      next++
    }
    return [date, units * price, putIn]
  })

  const [finish, worth] = series[series.length - 1]

  return {
    symbol: data.symbol,
    name: data.name,
    invested: putIn,
    worth,
    gain: worth - putIn,
    returnPercentage: (worth - putIn) / putIn * 100,
    annual: annual(amount, purchases, finish, worth),
    worstDrop: worstDrop(rows),
    bestYear: bestYear(rows),
    series
  }
}

async function load(symbol) {
  try {
    return await chart(symbol)
  } catch {
    return null
  }
}

export async function backtest({ amount, frequency, start, symbols, asOf = today() }) {
  const charts = await Promise.all(symbols.slice(0, LIMIT).map(load))
  const currencies = [...new Set(charts.filter(Boolean).map((data) => data.currency).filter((currency) => currency !== "EUR"))]
  const pairs = await Promise.all(currencies.map((currency) => load(`EUR${currency}=X`)))
  const rates = Object.fromEntries(currencies.map((currency, index) => [currency, pairs[index]]))
  const dates = schedule(start, frequency, asOf)

  const results = []
  const missing = []
  const unavailable = []

  symbols.slice(0, LIMIT).forEach((symbol, index) => {
    const data = charts[index]
    if (!data || (data.currency !== "EUR" && !rates[data.currency])) {
      unavailable.push(symbol)
      return
    }

    const outcome = result(data, rowsInEuros(data, rates[data.currency], start, asOf), amount, dates)
    if (outcome) results.push(outcome)
    else missing.push(symbol)
  })

  return { amount, frequency, start, results, missing, unavailable }
}
